/**
 * Accessibility utilities for scroll and focus management
 */

/**
 * Scrolls to an element and sets focus for keyboard navigation
 * Uses modern scrollend event when available, falls back to immediate focus
 */
export const scrollToAndFocus = (
  targetId: string,
  scrollOptions: ScrollIntoViewOptions = { behavior: "smooth", block: "start" }
): void => {
  const element = document.getElementById(targetId);
  if (!element) return;

  // For instant scrolling, focus immediately
  if (scrollOptions.behavior !== "smooth") {
    element.scrollIntoView(scrollOptions);
    element.focus();
    return;
  }

  // Use modern scrollend event if available
  if ("onscrollend" in window) {
    const handleScrollEnd = () => {
      element.focus();
      window.removeEventListener("scrollend", handleScrollEnd);
    };
    window.addEventListener("scrollend", handleScrollEnd);
    element.scrollIntoView(scrollOptions);
    return;
  }

  // Fallback: focus immediately for older browsers
  element.scrollIntoView(scrollOptions);
  element.focus();
};

/**
 * Scrolls to an element with offset and sets focus
 * Used for precise positioning with fixed headers
 */
export const scrollToElementWithOffset = (
  element: HTMLElement,
  offset = 0,
  behavior: "smooth" | "auto" = "smooth"
): void => {
  if (!element) return;

  const targetPosition =
    window.scrollY + element.getBoundingClientRect().top - offset;

  window.scrollTo({ top: targetPosition, behavior });

  // Handle focus based on scroll behavior
  if (behavior === "smooth" && "onscrollend" in window) {
    const handleScrollEnd = () => {
      element.focus();
      window.removeEventListener("scrollend", handleScrollEnd);
    };
    window.addEventListener("scrollend", handleScrollEnd);
  } else {
    element.focus();
  }
};

/**
 * Makes an element focusable and generates consistent category IDs
 */
export const makeFocusable = (
  element: HTMLElement,
  tabIndex = -1
): void => {
  if (!element.hasAttribute("tabindex")) {
    element.setAttribute("tabindex", tabIndex.toString());
  }
};

export const generateCategoryId = (categoryId: string | number): string =>
  `category-${categoryId}`;
